import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Printer } from "lucide-react";

export function CuentasTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    authAxiosRef.current.get("/orders?limit=200").then(res => { setOrders(res.data.filter(o => o.table_number)); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (selectedAccount) {
    const order = selectedAccount;
    return (
      <div data-testid="account-detail">
        <div className="flex items-center gap-2 text-sm mb-4">
          <button className="text-emerald-700 hover:underline" onClick={() => setSelectedAccount(null)}>Inicio</button>
          <span className="text-gray-400">/</span>
          <button className="text-emerald-700 hover:underline" onClick={() => setSelectedAccount(null)}>Cuentas</button>
          <span className="text-gray-400">/</span>
          <span className="font-medium">Cuenta #{order.id.slice(-6).toUpperCase()}</span>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="flex gap-8 text-sm">
            <div><span className="font-bold">Mesa</span><p className="text-emerald-700">{order.table_number ? `MESA ${String(order.table_number).padStart(2, "0")}` : "BALCAO"}</p></div>
            <div><span className="font-bold">Fecha</span><p>{new Date(order.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p></div>
            <div><span className="font-bold">Pedidos</span><p>{order.items?.length || 0}</p></div>
            <div><span className="font-bold">Total</span><p>{formatPrice(order.total)}</p></div>
            <div><span className="font-bold">Pago pendiente</span><p>{formatPrice(order.status === "delivered" ? 0 : order.total)}</p></div>
            <div><span className="font-bold">Link para pagamentos</span><a href="#" className="text-emerald-700 hover:underline text-sm block">Abrir link</a></div>
          </div>
        </div>

        <Button className="bg-orange-500 hover:bg-orange-600 text-white gap-2 font-bold mb-4" data-testid="print-cuenta-btn">
          <Printer className="w-4 h-4" /> IMPRIMIR
        </Button>

        <div className="flex gap-6">
          <div className="flex-1">
            <div className="border-b mb-3 pb-2 flex gap-6">
              {["PAGO", "DESCUENTO", "PEDIDO", "NFC-E"].map(t => (
                <span key={t} className="text-xs font-bold uppercase text-gray-500 cursor-pointer hover:text-gray-700">{t}</span>
              ))}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase">
                  <th className="text-left p-2 font-medium">Hora</th>
                  <th className="text-left p-2 font-medium">Pago</th>
                  <th className="text-center p-2 font-medium">Cantidad</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-left p-2 font-medium">Responsable</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2">{new Date(order.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="p-2">Cabal ({order.payment_method === "card" ? "Crédito" : "Efectivo"})</td>
                  <td className="p-2 text-center">{formatPrice(order.total)}</td>
                  <td className="p-2"><Badge className="bg-emerald-100 text-emerald-700">Acepto</Badge></td>
                  <td className="p-2">KAISO</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="w-[350px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase">
                  <th className="text-center p-2 font-medium w-10">Ctd</th>
                  <th className="text-left p-2 font-medium">Descripción</th>
                  <th className="text-right p-2 font-medium">Valor</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td className="p-2 text-center">{item.quantity}</td>
                    <td className="p-2">{item.product_name}</td>
                    <td className="p-2 text-right">
                      <div>{formatPrice(item.quantity * item.unit_price)}</div>
                      <div className="text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })} {new Date(order.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t">
                <tr><td colSpan={2} className="p-2 text-right text-gray-600">Subtotal</td><td className="p-2 text-right">{formatPrice(order.total)}</td></tr>
                <tr><td colSpan={2} className="p-2 text-right font-bold">Total</td><td className="p-2 text-right font-bold">{formatPrice(order.total)}</td></tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingScreen />;

  return (
    <div data-testid="cuentas-tab">
      <div className="flex items-center gap-3 mb-4">
        <Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs bg-white border-gray-200" data-testid="search-cuentas" />
      </div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
              <th className="text-left p-3 font-medium">Pedido</th>
              <th className="text-left p-3 font-medium">Mesa</th>
              <th className="text-left p-3 font-medium">Fecha</th>
              <th className="text-left p-3 font-medium">Cliente</th>
              <th className="text-center p-3 font-medium">Items</th>
              <th className="text-right p-3 font-medium">Total</th>
              <th className="text-left p-3 font-medium">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {orders.filter(o => !search || (o.customer_name || "").toLowerCase().includes(search.toLowerCase()) || o.id.includes(search)).map(o => (
              <tr key={o.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedAccount(o)} data-testid={`cuenta-row-${o.id.slice(-6)}`}>
                <td className="p-3 font-mono font-bold text-emerald-700">#{o.id.slice(-6).toUpperCase()}</td>
                <td className="p-3">Mesa {o.table_number}</td>
                <td className="p-3 text-gray-600">{new Date(o.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                <td className="p-3">{o.customer_name || "-"}</td>
                <td className="p-3 text-center">{o.items?.length || 0}</td>
                <td className="p-3 text-right font-bold">{formatPrice(o.total)}</td>
                <td className="p-3">
                  <Badge className={["delivered", "cancelled"].includes(o.status) ? "bg-gray-100 text-gray-500" : "bg-emerald-100 text-emerald-700"}>
                    {["delivered", "cancelled"].includes(o.status) ? "Cerrada" : "Abierta"}
                  </Badge>
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-gray-400">Sin cuentas de mesa</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
